import {useCallback, useState} from 'react';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';

import {
    getHttpIncidentFilters,
    HTTP_INCIDENTS_DEFAULT_SORT,
    HTTP_INCIDENTS_ORDER_FIELDS,
} from 'features/http/lib/httpFilters';
import {buildHttpIncidentFilters, DEFAULT_INCIDENT_DURATION} from 'features/http/lib/httpIncidentFiltersView';
import HttpIncidentFilters from 'features/http/model/HttpIncident/IHttpIncidentFilters';
import {IHttpIncidentFiltersForm} from 'features/http/model/HttpIncident/IHttpIncidentFiltersForm';
import IOrderBy from 'shared/model/IOrderBy';
import {setSearchParamsFromFilters, sortToUrlFilters} from 'shared/lib/filterHelper';
import {useSort} from 'shared/lib/hooks/useSort';

interface IUseHttpIncidentFiltersFormParams {
    onClose: () => void,
    isExport?: boolean,
    onExportSubmit?: SubmitHandler<IHttpIncidentFiltersForm>,
}

export interface IUseHttpIncidentFiltersForm {
    form: UseFormReturn<IHttpIncidentFiltersForm>,
    filters: HttpIncidentFilters,
    sort: IOrderBy | undefined,
    durationValue: number,
    handleDurationChange: (_value: number) => void,
    clearResolvedFilter: () => void,
    submit: ReturnType<UseFormReturn<IHttpIncidentFiltersForm>['handleSubmit']>,
}

export const useHttpIncidentFiltersForm = ({
    onClose,
    isExport,
    onExportSubmit,
}: IUseHttpIncidentFiltersFormParams): IUseHttpIncidentFiltersForm => {
    const form = useForm<IHttpIncidentFiltersForm>({mode: 'onSubmit'});
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = getHttpIncidentFilters(searchParams);
    const [sort] = useSort({defaultSort: HTTP_INCIDENTS_DEFAULT_SORT, sortFields: HTTP_INCIDENTS_ORDER_FIELDS});
    const [durationValue, setDurationValue] = useState(filters.duration || DEFAULT_INCIDENT_DURATION);

    const handleFilterSubmit: SubmitHandler<IHttpIncidentFiltersForm> = async(values) => {
        setSearchParamsFromFilters(searchParams, {
            ...buildHttpIncidentFilters(values),
            ...sortToUrlFilters(values.orderBy),
        });
        searchParams.set('page', '1');
        setSearchParams(searchParams);
        onClose();
    };

    const handleFormSubmit: SubmitHandler<IHttpIncidentFiltersForm> = async(data) => {
        if (isExport && onExportSubmit) {
            await onExportSubmit(data);
        } else {
            await handleFilterSubmit(data);
        }
    };

    const handleDurationChange = useCallback((value: number) => {
        setDurationValue(value);
    }, []);

    const clearResolvedFilter = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('resolved');
        setSearchParams(newParams);
    };

    return {
        form,
        filters,
        sort,
        durationValue,
        handleDurationChange,
        clearResolvedFilter,
        submit: form.handleSubmit(handleFormSubmit),
    };
};

export default useHttpIncidentFiltersForm;
